import React, { Suspense, lazy, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import DashboardHeader from "@/components/DashboardHeader";
import { MobileTopBar } from "@/components/MobileTopBar";
import MealTimingCoachTip from "@/components/MealTimingCoachTip";
import SEO from "@/components/SEO";
import { Loader2, BookOpen, Sparkles, BarChart3, Pill, Dumbbell, Activity, History } from "lucide-react";
import AdaptiveGroup from "@/components/dashboard/AdaptiveGroup";  // iter1077
import useIsDesktop from "@/hooks/useIsDesktop";  // iter1077
import DiaryQuickNav from "@/components/diary/DiaryQuickNav";
import DiaryGuide from "@/components/diary/DiaryGuide";
import SaveToPmosButton from "@/components/womens_health/SaveToPmosButton";
import DashboardWidget from "@/dashboard/DashboardWidget";  // iter553 — customizable visibility
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

// QuickNav сега работи в рамките на отделните табове — линковете водят към съответната секция.
const QUICK_NAV_SECTIONS = [
  { id: "diary-section-plate", label_bg: "Симулатор", label_en: "Plate Sim", tab: "log" },
  { id: "diary-section-twin", label_bg: "Digital Twin", label_en: "Digital Twin", tab: "log" },
  { id: "diary-section-meallog", label_bg: "Дневник", label_en: "Meal Log", tab: "log" },
  { id: "diary-section-patterns", label_bg: "Модели", label_en: "Patterns", tab: "analysis" },
  { id: "diary-section-deficiency", label_bg: "Дефицити", label_en: "Deficiency", tab: "analysis" },
  { id: "diary-section-planner", label_bg: "AI Planner", label_en: "AI Planner", tab: "ai" },
  { id: "diary-section-review", label_bg: "Преглед", label_en: "Plan Review", tab: "ai" },
  { id: "diary-section-history", label_bg: "История", label_en: "History", tab: "log" },
  { id: "diary-section-intelligence", label_bg: "Умно хранене", label_en: "Smart Nutrition", tab: "ai" },
  { id: "diary-section-weekly", label_bg: "Седмично", label_en: "Weekly", tab: "analysis" },
];

const MealLog = lazy(() => import("@/components/MealLog"));
const MealIntelligence = lazy(() => import("@/components/MealIntelligence"));
const MealHistoryPanel = lazy(() => import("@/components/MealHistoryPanel"));
import DiaryDateNavigator from "@/components/diary/DiaryDateNavigator";
const WeeklyChart = lazy(() => import("@/components/WeeklyChart"));
const DietaryPatternsCard = lazy(() => import("@/components/DietaryPatternsCard"));
const NutrientDeficiencyPanel = lazy(() => import("@/components/NutrientDeficiencyPanel"));
const DailyRecommendationCard = lazy(() => import("@/components/DailyRecommendationCard"));
const PlateSimulator = lazy(() => import("@/components/PlateSimulator"));
const DigitalTwinPanel = lazy(() => import("@/components/DigitalTwinPanel"));
const MealPlanner = lazy(() => import("@/components/MealPlanner"));
const MealPlanReview = lazy(() => import("@/components/MealPlanReview"));
const InsulinLoadTrend = lazy(() => import("@/components/InsulinLoadTrend"));
const Col3DailyTracker = lazy(() => import("@/components/diary/Col3DailyTracker"));
const SmartStackWidget = lazy(() => import("@/components/diary/SmartStackWidget"));
const Col3FoodCatalogueWidget = lazy(() => import("@/components/diary/Col3FoodCatalogueWidget"));
const MealImportCard = lazy(() => import("@/components/diary/MealImportCard"));
const SupplementLogCard = lazy(() => import("@/components/diary/SupplementLogCard"));
const ActivityTracker = lazy(() => import("@/components/ActivityTracker"));  // Feb 2026 — moved from Dashboard, влияе на PlateSimulator

/**
 * Diary Page — iter427 рефакторинг:
 * 3 главни таба (Дневен лог · AI препоръки · Анализ) с persistent tab state
 * в localStorage. QuickNav остава достъпен за бърз jump между секции.
 */
export default function DiaryPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const isDesktop = useIsDesktop(768);  // iter1077 — сгъваеми раздели на телефон

  const bg = language === "bg";
  const [searchParams, setSearchParams] = useSearchParams();
  const demoIntent = searchParams.get("demo");
  const focusIntent = searchParams.get("focus");  // iter479 — plate-simulator focus
  const prefillIntent = searchParams.get("prefill");  // iter739 — BHI food alias to preload search
  const batchIntent = searchParams.get("batch");  // iter740 — read sessionStorage batch list

  const [activeTab, setActiveTab] = useState(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && ["log", "ai", "analysis"].includes(urlTab)) return urlTab;
    if (typeof window === "undefined") return "log";
    try { return localStorage.getItem("diary-active-tab") || "log"; } catch { return "log"; }
  });

  // iter959 — Selected diary date. null = today. Persisted only in URL
  // (not localStorage) so refreshing brings the user back to "today" by
  // default. Values in YYYY-MM-DD, backend enforces max 30 days back.
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = searchParams.get("date");
    return d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
  });

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (selectedDate) next.set("date", selectedDate); else next.delete("date");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    try { localStorage.setItem("diary-active-tab", activeTab); } catch {}
    const next = new URLSearchParams(searchParams);
    if (activeTab === "log") next.delete("tab"); else next.set("tab", activeTab);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // iter230 — Scroll & highlight when arriving with ?demo=
  // iter479 — Also handle ?focus=plate-simulator from AI menu modal.
  useEffect(() => {
    const isDemo = !!demoIntent;
    const isFocus = focusIntent === "plate-simulator";
    if (!isDemo && !isFocus) return;
    const targetId = isFocus
      ? "plate-simulator-anchor"
      : demoIntent === "digital-twin"
      ? "digital-twin-panel-anchor"
      : demoIntent === "plate"
      ? "plate-simulator-anchor"
      : null;
    if (!targetId) return;
    setActiveTab("log");
    const tryScroll = (attempts = 0) => {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("demo-highlight-ring");
        setTimeout(() => el.classList.remove("demo-highlight-ring"), 4000);
        // iter739 — After scrolling to the plate simulator, if BHI sent us
        // here with a food alias, open the meal-log search overlay with
        // that alias pre-filled so the patient can add it in one click.
        if (isFocus && prefillIntent) {
          setTimeout(() => {
            try {
              window.dispatchEvent(new CustomEvent("meal-log:focus-search", {
                detail: { query: prefillIntent },
              }));
            } catch { /* ignore */ }
          }, 900);
        }
        // iter740 — Batch mode: read sessionStorage bhi_batch_add and
        // dispatch focus-search for the FIRST alias. Once the user adds
        // it, `meal-added` event fires — we pop and continue.
        if (isFocus && batchIntent === "bhi") {
          const runNext = () => {
            let raw = null;
            try { raw = sessionStorage.getItem("bhi_batch_add"); } catch { /* ignore */ }
            if (!raw) return;
            let parsed;
            try { parsed = JSON.parse(raw); } catch { return; }
            const list = Array.isArray(parsed?.aliases) ? parsed.aliases : [];
            if (!list.length) {
              try { sessionStorage.removeItem("bhi_batch_add"); } catch { /* ignore */ }
              return;
            }
            const [next, ...rest] = list;
            try {
              window.dispatchEvent(new CustomEvent("meal-log:focus-search", {
                detail: { query: next },
              }));
            } catch { /* ignore */ }
            try {
              sessionStorage.setItem("bhi_batch_add", JSON.stringify({ ...parsed, aliases: rest }));
            } catch { /* ignore */ }
          };
          const onMealAdded = () => setTimeout(runNext, 600);
          window.addEventListener("meal-added", onMealAdded);
          // Kick off the first one after the scroll settles.
          setTimeout(runNext, 900);
          // Cleanup when user leaves the page.
          const cleanup = () => window.removeEventListener("meal-added", onMealAdded);
          window.addEventListener("beforeunload", cleanup, { once: true });
        }
      } else if (attempts < 25) {
        setTimeout(() => tryScroll(attempts + 1), 200);
      }
    };
    setTimeout(() => tryScroll(), 800);
  }, [demoIntent, focusIntent, prefillIntent, batchIntent]);

  // Filter quick-nav links to current tab only
  const currentTabNavSections = QUICK_NAV_SECTIONS.filter(s => s.tab === activeTab);

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-gray-900"}`}
      data-testid="diary-page">
      <SEO
        title={bg ? "Умно хранене — Nutramilo" : "Smart Eating — Nutramilo"}
        description={bg ? "Личен водач в храненето: симулатор на чинията, дигитален близнак, съставяне на меню и хранителен профил." : "Personal AI nutrition navigator: plate simulator, digital twin, AI menus and your nutrition profile."}
        path="/dashboard/diary"
      />
      <DashboardHeader />
      <MobileTopBar title={bg ? "Умно хранене" : "Smart Eating"} fallbackTo="/dashboard" testid="diary-mobile-topbar" />
      {currentTabNavSections.length > 0 && <DiaryQuickNav sections={currentTabNavSections} />}
      <DiaryGuide />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <header className="mb-5">
          <h1 className="text-xl sm:text-2xl font-bold">
            {bg ? "Умно хранене" : "Smart Eating"}
          </h1>
          {/* iter593 — UX принцип: AI навигатор, не дневник за ръчно въвеждане */}
          <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`} data-testid="diary-positioning-line">
            {bg ? "Личен водач в храненето — разберете какво прави всяко хранене с тялото Ви."
              : "Your personal AI nutrition navigator — understand what every meal does to your body."}
          </p>
        </header>

        {/* iter1094 — Cycle-aware meal timing coach tip. Показва се само ако
            backend върне hint (жени с cycle данни или всеки в опасни часове). */}
        <MealTimingCoachTip />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="diary-tabs">
          {/* iter1090 — sticky tabs on mobile: остават видими под MobileTopBar (56px) */}
          <TabsList className={`flex w-full h-auto p-1 rounded-2xl gap-1 mb-5 overflow-x-auto sticky top-[52px] md:static z-30 backdrop-blur-xl ${
            isDark ? "bg-slate-900/80 border border-slate-800" : "bg-slate-100/90 border border-slate-200"
          }`}>
            <TabsTrigger value="log" data-testid="diary-tab-log"
              className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-bold">
              <BookOpen className="w-3.5 h-3.5" />
              {bg ? "Днес" : "Today"}
            </TabsTrigger>
            <TabsTrigger value="ai" data-testid="diary-tab-ai"
              className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-bold">
              <Sparkles className="w-3.5 h-3.5" />
              {bg ? "Умно хранене" : "Meal Intelligence"}
            </TabsTrigger>
            <TabsTrigger value="analysis" data-testid="diary-tab-analysis"
              className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-bold">
              <BarChart3 className="w-3.5 h-3.5" />
              {bg ? "Моят хранителен профил" : "My nutrition profile"}
            </TabsTrigger>
          </TabsList>

          <Suspense fallback={<div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>}>
            {/* === Tab 1: Днес — Hero: Plate Simulator → Digital Twin → препоръка → дневник === */}
            <TabsContent value="log" className="space-y-4 sm:space-y-5 mt-0" data-testid="diary-tab-content-log">
              {/* iter593 — HERO: „Какво ще стане, ако изям това?" — отговор в първите 5 секунди */}
              <div id="diary-section-plate" className="rounded-3xl">
                <div
                  id="plate-simulator-anchor"
                  className={`rounded-3xl p-[2px]`}
                  style={{ background: "linear-gradient(135deg, #EC4899, #8B5CF6)" }}
                  data-testid="plate-hero-wrap"
                >
                  <div className={`rounded-[calc(1.5rem-2px)] p-3 sm:p-4 ${isDark ? "bg-[hsl(222,47%,7%)]" : "bg-white"}`}>
                    <div className="mb-3">
                      <h2 className={`text-lg sm:text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`} data-testid="plate-hero-title">
                        {bg ? "Какво ще стане, ако изям това?" : "What happens if I eat this?"}
                      </h2>
                      <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {bg
                          ? "Сложи храните в чинията и виж веднага инсулиновия отговор и ефекта върху целите ти."
                          : "Put foods on the plate and instantly see the insulin response and the effect on your goals."}
                      </p>
                    </div>
                    <PlateSimulator />
                    <div className="mt-2 flex justify-end">
                      <SaveToPmosButton
                        module="plate_simulator"
                        bg={bg}
                        payload={{
                          timestamp: new Date().toISOString(),
                          note: "Plate Simulator snapshot",
                        }}
                        testId="plate-save-to-pmos"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* iter593 — Добавките остават до симулатора: включват се в оценката на чинията
                  iter1077 — на телефон разделът е сгъваем (страницата беше 4 600 px) */}
              <div id="diary-section-supplements" className="rounded-3xl">
                <AdaptiveGroup
                  plain={isDesktop}
                  title={bg ? "Хранителни добавки" : "Supplements"}
                  icon={Pill}
                  color="#8B5CF6"
                  storageKey="diary-supplements"
                  testId="mobile-fold-diary-supplements"
                >
                <p className={`text-[11px] mb-1.5 px-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  {bg ? "↑ Добавките по-долу се включват в оценката на симулатора." : "↑ Supplements below are included in the simulator scoring."}
                </p>
                <SupplementLogCard />
                </AdaptiveGroup>
              </div>

              {/* Feb 2026 — Тренировки и медитации се вливат в оценката (cortisol, insulin sensitivity, kcal_net) */}
              <div id="diary-section-activity" className="rounded-3xl">
                <AdaptiveGroup
                  plain={isDesktop}
                  title={bg ? "Движение и медитации" : "Activity"}
                  icon={Dumbbell}
                  color="#F59E0B"
                  storageKey="diary-activity"
                  testId="mobile-fold-diary-activity"
                >
                <p className={`text-[11px] mb-1.5 px-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  {bg
                    ? "↑ Тренировките и медитациите влияят на cortisol axis, инсулинова чувствителност и net kcal в симулатора."
                    : "↑ Workouts and meditations influence the cortisol axis, insulin sensitivity and net kcal in the simulator."}
                </p>
                <ActivityTracker bg={bg} isDark={isDark} />
                </AdaptiveGroup>
              </div>

              {/* iter593 — Digital Twin: ключова функционалност, не обикновен widget */}
              <div id="diary-section-twin" className="rounded-3xl">
                <AdaptiveGroup
                  plain={isDesktop}
                  title={bg ? "Вашият дигитален близнак" : "Your digital twin"}
                  icon={Activity}
                  color="#06B6D4"
                  storageKey="diary-twin"
                  testId="mobile-fold-diary-twin"
                >
                <div
                  id="digital-twin-panel-anchor"
                  className="rounded-3xl p-[2px]"
                  style={{ background: "linear-gradient(135deg, #06B6D4, #8B5CF6)" }}
                  data-testid="twin-elevated-wrap"
                >
                  <div className={`rounded-[calc(1.5rem-2px)] p-3 sm:p-4 ${isDark ? "bg-[hsl(222,47%,7%)]" : "bg-white"}`}>
                    <div className="mb-3 flex items-center gap-2 flex-wrap">
                      <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {bg ? "Всяко хранене се влива във Вашия дигитален здравен близнак." : "Every meal flows into your digital health twin."}
                      </p>
                    </div>
                    <DigitalTwinPanel />
                  </div>
                </div>
                </AdaptiveGroup>
              </div>

              <DailyRecommendationCard />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                <div className="space-y-4">
                  <div id="diary-section-meallog">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <h3 className={`text-sm font-bold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                        {bg ? "🍽 Хранителен дневник" : "🍽 Meal Log"}
                      </h3>
                      <DiaryDateNavigator
                        selectedDate={selectedDate}
                        onDateChange={setSelectedDate}
                        bg={bg}
                        isDark={isDark}
                      />
                    </div>
                    <MealLog selectedDate={selectedDate} />
                    <div className="mt-2 flex justify-end">
                      <SaveToPmosButton
                        module="meal_log"
                        bg={bg}
                        payload={{
                          snapshot_date: selectedDate || new Date().toISOString().slice(0, 10),
                          note: selectedDate ? `Meal log snapshot for ${selectedDate}` : "Meal log snapshot",
                        }}
                        testId="meallog-save-to-pmos"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div id="diary-section-history">
                    <AdaptiveGroup
                      plain={isDesktop}
                      title={bg ? "Минали хранения" : "Meal history"}
                      icon={History}
                      color="#EC4899"
                      storageKey="diary-history"
                      testId="mobile-fold-diary-history"
                    >
                      <MealHistoryPanel />
                    </AdaptiveGroup>
                  </div>
                </div>
              </div>

              {/* iter593 — Внос от външни приложения: помощен инструмент, сгъваем най-долу */}
              <details id="diary-section-import" className="rounded-3xl group" data-testid="diary-import-collapsible">
                <summary className={`cursor-pointer select-none rounded-2xl border px-4 py-3 text-sm font-semibold flex items-center justify-between ${
                  isDark ? "bg-slate-900/60 border-slate-800 text-gray-300" : "bg-white border-slate-200 text-gray-700"
                }`}>
                  <span>🔧 {bg ? "Инструменти · Внос от външни приложения" : "Tools · Import from external apps"}</span>
                  <span className="transition-transform group-open:rotate-180">▾</span>
                </summary>
                <div className="mt-3">
                  <MealImportCard />
                </div>
              </details>
            </TabsContent>

            {/* === Tab 2: AI препоръки — Intelligence, Planner, Review, SmartStack === */}
            <TabsContent value="ai" className="space-y-4 sm:space-y-5 mt-0" data-testid="diary-tab-content-ai">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                <div className="space-y-4">
                  <div id="diary-section-intelligence"><MealIntelligence /></div>
                  <div id="diary-section-planner"><MealPlanner /></div>
                </div>
                <div className="space-y-4">
                  <DashboardWidget id="meal-plan-review">
                    <div id="diary-section-review"><MealPlanReview /></div>
                  </DashboardWidget>
                  <DashboardWidget id="smart-stack-widget">
                    <div id="diary-section-smart-stack"><SmartStackWidget /></div>
                  </DashboardWidget>
                </div>
              </div>
            </TabsContent>

            {/* === Tab 3: Анализ — Trends, Patterns, Deficiency, Col3 === */}
            <TabsContent value="analysis" className="space-y-4 sm:space-y-5 mt-0" data-testid="diary-tab-content-analysis">
              <DashboardWidget id="weekly-il-trend">
                <div id="diary-section-il-trend"><InsulinLoadTrend /></div>
              </DashboardWidget>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                <div className="space-y-4">
                  <DashboardWidget id="dietary-patterns">
                    <div id="diary-section-patterns"><DietaryPatternsCard /></div>
                  </DashboardWidget>
                  <DashboardWidget id="nutrient-deficiency-diary">
                    <div id="diary-section-deficiency"><NutrientDeficiencyPanel /></div>
                  </DashboardWidget>
                </div>
                <div className="space-y-4">
                  <DashboardWidget id="weekly-chart">
                    <div id="diary-section-weekly"><WeeklyChart /></div>
                  </DashboardWidget>
                  <DashboardWidget id="col3-daily-tracker">
                    <div id="diary-section-col3-tracker"><Col3DailyTracker /></div>
                  </DashboardWidget>
                  <DashboardWidget id="col3-foods-catalogue">
                    <div id="diary-section-col3-foods-by-nutrient"><Col3FoodCatalogueWidget /></div>
                  </DashboardWidget>
                </div>
              </div>
            </TabsContent>
          </Suspense>
        </Tabs>
      </main>
    </div>
  );
}
